import { useState, useEffect } from 'react';
import { getSupplierCostDocumentUrl } from '../services/supplierCostDocumentService';
import {
  DOCUMENT_TYPE_CONFIG,
  OCR_STATUS_CONFIG,
  formatCurrency,
  type SupplierCostDocumentView,
} from '../types';

interface SupplierCostDocumentCardProps {
  document: SupplierCostDocumentView;
  signedUrl?: string;
  onClick?: () => void;
  onDelete?: () => void;
  showDeleteButton?: boolean;
}

export function SupplierCostDocumentCard({
  document,
  signedUrl: propSignedUrl,
  onClick,
  onDelete,
  showDeleteButton = false,
}: SupplierCostDocumentCardProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(propSignedUrl || null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(!propSignedUrl);

  // Determine file type from path
  const isPdf = document.filePath.toLowerCase().endsWith('.pdf');
  const isImage = !isPdf;

  useEffect(() => {
    if (propSignedUrl) {
      setSignedUrl(propSignedUrl);
      setIsLoadingUrl(false);
      return;
    }

    // Only load URLs for images
    if (!isImage) {
      setIsLoadingUrl(false);
      return;
    }

    let cancelled = false;
    const loadUrl = async () => {
      try {
        const url = await getSupplierCostDocumentUrl(document.filePath);
        if (!cancelled) {
          setSignedUrl(url);
        }
      } catch (error) {
        console.error('Error loading document URL:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingUrl(false);
        }
      }
    };

    loadUrl();

    return () => {
      cancelled = true;
    };
  }, [document.filePath, propSignedUrl, isImage]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const typeConfig = DOCUMENT_TYPE_CONFIG[document.documentType];
  const ocrConfig = OCR_STATUS_CONFIG[document.ocrStatus as keyof typeof OCR_STATUS_CONFIG];

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className="group relative bg-white rounded-lg border border-secondary-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary-500 hover:shadow-md transition-all"
      onClick={onClick}
    >
      {/* Preview Area */}
      <div className="relative aspect-square bg-secondary-100">
        {isImage && signedUrl ? (
          <img
            src={signedUrl}
            alt={document.supplierName || 'Document'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : isImage && isLoadingUrl ? (
          <div className="w-full h-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-secondary-400 animate-pulse"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        ) : isPdf ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-secondary-400">
            <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h2a1.5 1.5 0 0 1 0 3H9v2H8v-5h.5zm2 2H9v-1h1.5a.5.5 0 0 1 0 1zM14 13h1.5a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-1.5 1.5H14v-5zm1 4h.5a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5H15v3zm-9 0h2v1H5v-5h1v4z" />
            </svg>
            <span className="text-xs mt-1">PDF</span>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-secondary-400">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="text-xs mt-1">File</span>
          </div>
        )}

        {/* Document Type Badge */}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-white/90 text-secondary-700 shadow-sm">
          {typeConfig.label}
        </div>

        {/* OCR Status Badge */}
        {ocrConfig && document.ocrStatus !== 'succeeded' && (
          <div className={`absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium ${ocrConfig.color}`}>
            {ocrConfig.label}
          </div>
        )}

        {/* Delete Button */}
        {showDeleteButton && onDelete && (
          <button
            onClick={handleDeleteClick}
            className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
            title="Delete"
          >
            <svg
              className="w-4 h-4 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Info Section */}
      <div className="p-3 space-y-2">
        {/* Supplier Name */}
        <p className="text-sm font-medium text-secondary-900 truncate" title={document.supplierName || 'No supplier assigned'}>
          {document.supplierName || <span className="text-secondary-400 italic">No supplier</span>}
        </p>

        {/* Amount */}
        {document.totalAmount !== null && (
          <p className="text-sm font-semibold text-primary-600">
            {formatCurrency(document.totalAmount, document.currency)}
          </p>
        )}

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-secondary-500">
          <span>{formatDate(document.createdAt)}</span>
          {document.vendorNameRaw && document.vendorNameRaw !== document.supplierName && (
            <span className="truncate ml-2" title={document.vendorNameRaw}>
              {document.vendorNameRaw}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
